import Router from 'next/router';
import { isNil } from 'lodash';
import { message } from 'antd';

import storeRematch from '@/store';
import api from '@/api';
import { api as apiv2 } from '@/api/api.v2';

import { updateTreeData, getCheckedItemCode, toParams, getParentKey, findItemByKey } from '@/components/Tree/lib';
import { InitStatusAll, InitStatuses } from '@/components/Tree/FilterList/statuses';
import { groupsList } from '@/components/Tree/CategoryList/categories';
import { mapNodeType } from '@/components/Tree/CategoryList/CategoryList';
import { isAllowNodeForModule } from '@/components/Tree/Node/permissions';

const treeModel = {
    state: {
        expandedKeys: [],
        firstInit: true,
        data: [],
        isShowFilter: false,
        isShowCategory: false,
        loading: false,
        error: false,
        filterStatusAll: InitStatusAll,
        filterStatusesList: InitStatuses,
        localFilterStatusAll: InitStatusAll,
        localFilterStatusesList: InitStatuses,
        treeLevelsKeys: [], // Возможные значения: ['develop', 'area', 'elevation', 'groupStructureWell', 'itemNotDeepWell'], требуется покрытие типами.
        groupFilterValue: getCheckedItemCode(groupsList),
        calculationObject: null,
        highlightPaths: [],
    },
    reducers: {
        'setExpandedKeys'(state, payload) {
            return { ...state, expandedKeys: payload };
        },
        'setFirstInit'(state, payload) {
            return { ...state, firstInit: payload };
        },
        'lists/setActiveItemKey': (state, payload) => {
            if (payload && state.calculationObject) {
                return { ...state, calculationObject: null };
            }
            return state;
        },
        'lists/setActiveGroupKey': (state, payload) => {
            if (payload && state.calculationObject) {
                return { ...state, calculationObject: null };
            }
            return state;
        },
        'setTreeData'(state, payload) {
            return { ...state, data: payload };
        },
        'setLoadingStatus'(state, payload) {
            return { ...state, loading: payload };
        },
        'setErrorStatus'(state, payload) {
            return { ...state, error: payload };
        },
        'setIsShowFilter'(state, payload) {
            return { ...state, isShowFilter: payload };
        },
        'setIsShowCategory'(state, payload) {
            return { ...state, isShowCategory: payload };
        },
        'setFilterStatusAll'(state, payload) {
            return { ...state, filterStatusAll: payload };
        },
        'setFilterStatusesList'(state, payload) {
            return { ...state, filterStatusesList: payload };
        },
        'setLocalFilterStatusAll'(state, payload) {
            return { ...state, localFilterStatusAll: payload };
        },
        'setLocalFilterStatusesList'(state, payload) {
            return { ...state, localFilterStatusesList: payload };
        },
        'setTreeLevelsKeys'(state, payload) {
            return { ...state, treeLevelsKeys: payload };
        },
        'setGroupFilterValue'(state, payload) {
            return { ...state, groupFilterValue: payload };
        },
        'setCalculationObject'(state, payload) {
            if (payload) {
                /*
                    toParams - метод, формирующий готовый объект для серверных запросов.
                    Так же есть свойство parents в виде массива,
                    в котором в иерархичном порядке представлены родительские узлы.
                */
                return { ...state, calculationObject: { ...payload, toParams: () => toParams(payload) } };
            }
            return { ...state, calculationObject: payload };
        },
        'setHighlightPaths'(state, payload) {
            return { ...state, highlightPaths: payload };
        },
    },
    effects: dispatch => ({
        async getUserSettings(payload, rootState) {
            const userSettingsResponse = await api.user.loadData('userSettings');
            if (typeof window !== 'undefined') {
                const stored = localStorage.getItem('expandedTreeKeys');
                console.log('stored:', stored);
                if (stored) {
                    dispatch.tree.setExpandedKeys(JSON.parse(stored));
                }
            }
            if (!userSettingsResponse) {
                throw new Error(
                    'Не удалось получить пользовательские настройки (userSettings): ',
                    userSettingsResponse,
                );
            }

            const arr = [];
            for (const [key, value] of Object.entries(userSettingsResponse.data)) {
                if (value) {
                    arr.push(mapNodeType[key]);
                }
            }
            dispatch.tree.setTreeLevelsKeys(arr);
            dispatch.tree.setFirstInit(false);

            // Инициализация настроек абсолютной глубины, если они пришли с сервера
            if (userSettingsResponse.data && 'is_abs_negative' in userSettingsResponse.data) {
                try {
                    const { absolutDepthsSettings, defaultAbsolutDepthsSettingsState } = await import(
                        '@/restore/global-settings/absolutDepths-settings'
                    );

                    const isAbsNegative = !!userSettingsResponse.data.is_abs_negative;

                    absolutDepthsSettings.isDisplayMinusAbsDepths = isAbsNegative;
                    defaultAbsolutDepthsSettingsState.isDisplayMinusAbsDepths = isAbsNegative;

                    if (typeof window !== 'undefined' && window.localStorage) {
                        localStorage.setItem(
                            'absolutDepthsSettings',
                            JSON.stringify({ isDisplayMinusAbsDepths: isAbsNegative }),
                        );
                    }
                } catch (error) {
                    console.warn('Ошибка инициализации настроек абсолютной глубины:', error);
                }
            }
        },
        async treeSearch({ searchKey, searchMode } = {}, rootState) {
            dispatch.tree.setLoadingStatus(true);
            const groupView = rootState.tree.groupFilterValue;

            const searchResults = await api.tree.treeSearch(searchKey, searchMode, groupView);
            dispatch.tree.setHighlightPaths(searchResults.data);

            dispatch.tree.setLoadingStatus(false);
        },
        async deleteProfile(item, rootState) {
            dispatch.tree.setLoadingStatus(true);

            await api.carottage.deleteProfile({ profileId: item.id });

            //обновление нужной ветки дерева
            const parentKey = getParentKey(item.key, rootState.tree.data);
            const parentItem = findItemByKey(parentKey, rootState.tree.data);
            this.fetchTreeData({ treeData: rootState.tree.data, key: parentKey, params: parentItem.urlParam });

            dispatch.tree.setLoadingStatus(false);
        },
        async fetchTreeData({ treeData, key, params } = {}, rootState) {
            dispatch.tree.setLoadingStatus(true);

            const groupView = rootState.tree.groupFilterValue;

            try {
                if (rootState.tree.firstInit) {
                    await dispatch.tree.getUserSettings();
                }

                const response = await api.tree.fetchData({ params, groupView });

                const data = updateTreeData(treeData, key, response);

                dispatch.tree.setTreeData(data);
                dispatch.tree.setLoadingStatus(false);
                dispatch.tree.setErrorStatus(false);
            } catch (error) {
                dispatch.tree.setLoadingStatus(false);
                dispatch.tree.setErrorStatus(true);
            }
        },

        async fetchWellsInfo(payload) {
            try {
                const body = {
                    well_ids: payload,
                };
                const response = await apiv2.tree.fetchWellInfoByIds(body);
                return response.data;
            } catch (error) {
                console.error('fetchWellsInfo', error);
                return [];
            }
        },

        async addToListsAsGroup(item, rootState) {
            const { key, title: groupTitle, urlParam, nodeType, id: nodeID, modTitle } = item;
            let { children } = item;

            const groupView = rootState.tree.groupFilterValue;
            const isGroupProfileView = nodeType === 'profileInWell';

            try {
                if (!children) {
                    if (!isGroupProfileView) {
                        children = await api.tree.fetchData({
                            params: urlParam,
                            groupView,
                        });
                    } else {
                        // ARM-3788
                        // Данный workaround добавлен по согласованию с бэкендом, так как
                        // сейчас нету отдельной возможности добавления профайлов как группу скважин,
                        // когда дерево в режиме просмотра "Профайлы"
                        // В связи с этим запрос меняется на запрос добавления профайлов,
                        // когда в режиме "Организации"

                        // FIXME: Добавить отдельную возможность добавления профайлов как группу скважин"
                        const processedUrlParams = {
                            nodeType: 'profile',
                            id: nodeID,
                            ids: item.parents.reduce((acc, item) => {
                                if (item.node === 'profileProfile') {
                                    acc.profile = nodeID;
                                    acc.groupProfile = item.id;
                                    return acc;
                                }
                                acc[item.node] = item.id;
                                return acc;
                            }, {}),
                        };
                        children = await api.tree.fetchData({
                            params: `?urlParam=${JSON.stringify(processedUrlParams)}`,
                            groupView: 'organization',
                        });
                    }
                }

                if (children?.length > 0) {
                    const idWells = children.map(elem => elem.id);
                    const info = await dispatch.lists.fetchWellsInfo(idWells);

                    const updatedChildren = children.reduce((acc, elem) => {
                        const { id, title } = elem;
                        const infoCurrentWell = info.find(item => item.id === id) ?? {};
                        if (!isNil(id)) {
                            acc.push({
                                ...elem,
                                ...infoCurrentWell,
                                id,
                                isLeaf: true,
                                key: `well_${id}_${nodeType}_${key}`,
                                node: 'well',
                                nodeType: 'well',
                                title: title,
                                urlParam: '',
                            });
                        }
                        return acc;
                    }, []);

                    await dispatch.lists.openNewGroupByItems({
                        data: updatedChildren,
                        id: nodeID,
                        type: nodeType,
                        groupTitle,
                        modTitle,
                    });
                    message.success(`${groupTitle} добавлен в список`);
                }
            } catch (error) {
                console.error(error);
                message.error(`Не удалось добавить ${groupTitle} в список`);
            }
        },
    }),
};

const isAllowNode = route => {
    const rootStore = storeRematch.getState();
    const setCalculationObject = storeRematch.dispatch.tree.setCalculationObject;
    const { node } = rootStore.tree.calculationObject ?? {};
    if (isAllowNodeForModule(route, node)) return;
    setCalculationObject(null);
};

Router.events.on('routeChangeComplete', isAllowNode);

export default treeModel;
